/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import React from 'react';
import { connect } from 'react-redux';
import {
    IMultiLevelDataServiceDataLayout,
    IMultiLevelDataset,
    IMultiLevelDatasetHandle
} from '../store/types/MultiLevelDatasetTypes';
import {
    IVisualizationDynamicProperties,
    IVisualizationTypeProperties
} from '../store/types/VisualizationTypes';
import { IState, ILinkOptions } from '../store/types/StateTypes';
import datasetTypes from '../enums/datasetTypes';
import { loadMultiLevelData } from '../actions/multiLevelDataActions';
import {
    updateDynamicProperties as updateDynamicPropertiesAction,
} from '../actions/visualizationActions';
import DataState from '../enums/DataState';
import AdHocMessageComponent from './AdHocMessageComponent';

interface IMultiLevelDataServiceComponentProperties {
    visualization: any, // TODO: Component<IMultiLevelDatasetVisualization>
    data: IMultiLevelDataset,
    controlsDataLoading: boolean
    staticProperties: any,
    dynamicProperties: IVisualizationDynamicProperties,
    levelsLayout: any,
    linkOptions?: ILinkOptions,
    dataState: string,
    dataError: null | any,
    deferredAction: JQuery.Deferred<any>,

    loadData: Function,
    updateDynamicProperties: (dynamicProperties: IVisualizationDynamicProperties) => void
}

class MultiLevelDataServiceComponentUnconnected
    extends React.Component<IMultiLevelDataServiceComponentProperties, never> {

    componentDidMount(): void {
        this.resolveDeferredAction();
    }

    componentDidUpdate(prevProps: Readonly<IMultiLevelDataServiceComponentProperties>, prevState: Readonly<never>, snapshot?: any): void {
        this.resolveDeferredAction();
    }

    resolveDeferredAction() {
        const { dataState, dataError, deferredAction } = this.props;

        if (dataState === DataState.LOADED) {
            deferredAction.resolve();
        } else if (dataState === DataState.ERROR) {
            deferredAction.reject(dataError);
        }
    }

    render() {
        const { visualization: Visualization } = this.props;
        const {
            data,
            controlsDataLoading,
            linkOptions,
            staticProperties,
            dynamicProperties,
            loadData,
            updateDynamicProperties
        } = this.props;

        const handle: IMultiLevelDatasetHandle = {
            requestDatasetUpdate: loadData,
            updateDynamicProperties: (properties: IVisualizationDynamicProperties) => {
                // eslint-disable-next-line no-unused-expressions
                Object.keys(properties).length && updateDynamicProperties(properties);
            }
        };

        if (!controlsDataLoading && !data.summary.complete) {
            // TODO optimize
            handle.requestDatasetUpdate(data.summary, 0, 0, 0);
        }

        if (data.summary.empty) {
            return (
                <AdHocMessageComponent />
            )
        }

        return (
            <Visualization
                dataset={data}
                staticProperties={staticProperties}
                dynamicProperties={dynamicProperties}
                linkOptions={linkOptions}
                handle={handle}
            />
        );
    }
}

const mapStateToProps = (state: IState) => {
    const visualization: IVisualizationTypeProperties = state.visualization[state.visualizationDescriptor.name];
    return {
        levelsLayout: state.levelsLayout,
        linkOptions: state.linkOptions,
        data: state.data[datasetTypes.MULTI_LEVEL],
        staticProperties: visualization.staticProperties,
        dynamicProperties: visualization.dynamicProperties,
        dataState: state.dataState,
        dataError: state.dataError,
        deferredAction: state.deferredAction
    };
};

const mapDispatchToProps = (dispatch: any) => ({
    loadData: (dataLayout: IMultiLevelDataServiceDataLayout, startIndex: number, count: number) => (
        dispatch(loadMultiLevelData(dataLayout, startIndex, count))
    ),
    updateDynamicProperties: (properties: IVisualizationDynamicProperties) => {
        dispatch(updateDynamicPropertiesAction(properties));
    }
});

const connectFn = connect(mapStateToProps, mapDispatchToProps);
const MultiLevelDataServiceComponent = connectFn(MultiLevelDataServiceComponentUnconnected);

export {
    MultiLevelDataServiceComponent,
    MultiLevelDataServiceComponentUnconnected,
    mapDispatchToProps,
    mapStateToProps
};
